import { Card, Select, Space, Table, Tag, Typography } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { getKPI } from '../kpi/list/model'
import { KPI } from '../kpi/list/types'
import { showNotification } from '../../../features/notifications'
import { getPercentageStatus } from '../shared/percentageStatus'

type QuarterlyRow = { userId: string; username: string; months: Array<number | null>; total: number | null }

export const QuarterlyKPIList = () => {
  const initialQuarter = dayjs().subtract(3, 'month').month(Math.floor(dayjs().subtract(3, 'month').month() / 3) * 3).startOf('month')
  const [year, setYear] = useState(initialQuarter.year())
  const [quarterNumber, setQuarterNumber] = useState(Math.floor(initialQuarter.month() / 3) + 1)
  const [rows, setRows] = useState<QuarterlyRow[]>([])
  const [loading, setLoading] = useState(false)
  const quarter = useMemo(() => dayjs(`${year}-01-01`).add((quarterNumber - 1) * 3, 'month'), [year, quarterNumber])
  const months = useMemo(() => [0, 1, 2].map((offset) => quarter.add(offset, 'month')), [quarter])
  const yearOptions = useMemo(() => Array.from({ length: 5 }, (_, index) => dayjs().year() - index).map((value) => ({ value, label: value })), [])

  useEffect(() => {
    let active = true
    setLoading(true)
    Promise.all(months.map((month) => getKPI({ query: { limit: '500', page: '1', where: JSON.stringify({ periodName: month.format('YYYY-MM') }) } })))
      .then((responses) => {
        if (!active) return
        const byUser = new Map<string, QuarterlyRow>()
        responses.forEach((response, index) => response.body.items.forEach((item: KPI) => {
          const row = byUser.get(item.userId) ?? { userId: item.userId, username: item.username, months: [null, null, null], total: null }
          row.months[index] = item.kpiPercent
          byUser.set(item.userId, row)
        }))
        setRows([...byUser.values()].map((row) => {
          const hasAllMonths = row.months.every((value): value is number => value !== null)
          return { ...row, total: hasAllMonths ? (row.months as number[]).reduce((sum, value) => sum + value, 0) / 3 : null }
        }))
      })
      .catch((error) => showNotification({
        type: 'error',
        message: 'Не удалось загрузить квартальный KPI',
        description: error?.body?.message ?? 'Попробуйте обновить страницу.',
      }))
      .finally(() => active && setLoading(false))
    return () => { active = false }
  }, [months])

  const pct = (value: number | null) => value === null ? '—' : `${value.toFixed(2)}%`
  return <Card title="Quarterly KPI" extra={<Space><Select value={year} options={yearOptions} onChange={setYear} style={{ width: 100 }} /><Select value={quarterNumber} options={[1, 2, 3, 4].map((value) => ({ value, label: `Q${value}` }))} onChange={setQuarterNumber} style={{ width: 80 }} /></Space>}>
    <Typography.Paragraph type="secondary">Итого = (месяц 1 + месяц 2 + месяц 3) / 3</Typography.Paragraph>
    <Table loading={loading} rowKey="userId" pagination={false} dataSource={rows} columns={[
      { title: 'Manager', dataIndex: 'username' },
      ...months.map((month, index) => ({ title: month.format('MMMM YYYY'), render: (_: unknown, row: QuarterlyRow) => pct(row.months[index]) })),
      { title: 'Итого', render: (_: unknown, row: QuarterlyRow) => row.total === null ? '—' : <Tag color={getPercentageStatus(row.total)} style={{ fontFamily: 'monospace', fontWeight: 600 }}>{pct(row.total)}</Tag> },
    ]} />
  </Card>
}
